<div class="bg-white dark:bg-slate-900 rounded-xl shadow dark:shadow-gray-700 p-4 sm:p-6">
  <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
    <div>
      <h2 class="text-xl sm:text-2xl font-semibold text-slate-900 dark:text-white mb-1">Review Your Booking</h2>
      <p class="text-sm text-slate-600 dark:text-slate-400">Step 4 of 6 • Complete Booking Summary</p>
    </div>
    <div class="mt-3 sm:mt-0">
      <div class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
        <i data-feather="check-circle" class="w-3 h-3 mr-1"></i>
        Ready for Payment
      </div>
    </div>
  </div>

  <div class="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
    <div class="flex items-start">
      <i data-feather="info" class="w-5 h-5 text-blue-500 mr-3 mt-0.5 flex-shrink-0"></i>
      <div>
        <h3 class="text-sm font-medium text-blue-900 dark:text-blue-200 mb-1">Review Your Details</h3>
        <p class="text-sm text-blue-700 dark:text-blue-300">
          Please carefully review all booking details below. You can edit any section by clicking the "Edit" button.
          Once you proceed to payment, changes may not be possible.
        </p>
      </div>
    </div>
  </div>

  <!-- Booking Summary Cards -->
  <div class="space-y-4 sm:space-y-6">

    <!-- Guest Details Card -->
    <div class="border border-gray-200 dark:border-gray-700 rounded-lg p-4 sm:p-6 bg-gradient-to-r from-gray-50 to-white dark:from-slate-800 dark:to-slate-900">
      <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <div class="flex items-center mb-2 sm:mb-0">
          <div class="w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mr-3">
            <i data-feather="user" class="w-5 h-5 text-red-600 dark:text-red-400"></i>
          </div>
          <div>
            <h3 class="text-lg font-semibold text-slate-900 dark:text-white">Guest Information</h3>
            <p class="text-sm text-gray-600 dark:text-gray-400">Primary guest and contact details</p>
          </div>
        </div>
        <button
          type="button"
          (click)="editStep(1)"
          class="text-sm text-red-500 hover:text-red-600 font-medium flex items-center px-3 py-1 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
          <i data-feather="edit-2" class="w-4 h-4 mr-1"></i>
          Edit Details
        </button>
      </div>

      <!-- Primary Guest Info -->
      <div class="bg-white dark:bg-slate-800 rounded-lg p-4 mb-4 border border-gray-100 dark:border-gray-700">
        <h4 class="text-sm font-medium text-gray-900 dark:text-white mb-3 flex items-center">
          <i data-feather="user-check" class="w-4 h-4 mr-2 text-green-600"></i>
          Primary Guest
        </h4>
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <p class="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Full Name</p>
            <p class="font-semibold text-slate-900 dark:text-white text-lg">
              {{ bookingData.guestDetails?.firstName }} {{ bookingData.guestDetails?.lastName }}
            </p>
          </div>
          <div>
            <p class="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Email Address</p>
            <div class="flex items-center gap-2">
              <p class="font-medium text-slate-900 dark:text-white">{{ bookingData.guestDetails?.email }}</p>
              <span *ngIf="bookingData.guestDetails?.emailVerified"
                    class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                <i data-feather="check" class="w-3 h-3 mr-1"></i>
                Verified
              </span>
              <span *ngIf="!bookingData.guestDetails?.emailVerified"
                    class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                <i data-feather="x" class="w-3 h-3 mr-1"></i>
                Not Verified
              </span>
            </div>
            <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">Confirmation will be sent here</p>
          </div>
          <div>
            <p class="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Phone Number</p>
            <p class="font-medium text-slate-900 dark:text-white">{{ bookingData.guestDetails?.phone }}</p>
            <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">For urgent contact</p>
          </div>
          <div>
            <p class="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Guestes</p>
            <div class="flex items-center space-x-4">
              <div class="flex items-center">
                <i data-feather="users" class="w-4 h-4 mr-1 text-blue-600"></i>
                <span class="font-semibold text-slate-900 dark:text-white">{{  bookingData.guests?.adults || 0  }}</span>
                <span class="text-sm text-gray-600 dark:text-gray-400 ml-1">Adult{{ totalGuests > 1 ? 's' : '' }}</span>
              </div>
              <div class="flex items-center">
                <i data-feather="users" class="w-4 h-4 mr-1 text-blue-600"></i>
                <span class="font-semibold text-slate-900 dark:text-white">{{  bookingData.guests?.children || 0  }}</span>
                <span class="text-sm text-gray-600 dark:text-gray-400 ml-1">Child{{ (bookingData.guests?.children || 0) > 1 ? 'ren' : '' }}</span>
              </div>
              <div class="flex items-center">
                <i data-feather="home" class="w-4 h-4 mr-1 text-green-600"></i>
                <span class="font-semibold text-slate-900 dark:text-white">{{ bookingData.guests?.rooms || 0 }}</span>
                <span class="text-sm text-gray-600 dark:text-gray-400 ml-1">Room{{ (bookingData.guests?.rooms || 0) > 1 ? 's' : '' }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Special Requests -->
      <div *ngIf="bookingData.guestDetails?.specialRequests" class="mt-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
        <h4 class="text-sm font-medium text-yellow-900 dark:text-yellow-200 mb-2 flex items-center">
          <i data-feather="message-circle" class="w-4 h-4 mr-2"></i>
          Special Requests
        </h4>
        <p class="text-sm text-yellow-800 dark:text-yellow-300">{{ bookingData.guestDetails?.specialRequests }}</p>
        <p class="text-xs text-yellow-600 dark:text-yellow-400 mt-2">We'll do our best to accommodate your requests (subject to availability)</p>
      </div>
    </div>

    <!-- Stay Details Card -->
    <div class="border border-gray-200 dark:border-gray-700 rounded-lg p-4 sm:p-6 bg-gradient-to-r from-blue-50 to-white dark:from-blue-900/10 dark:to-slate-900">
      <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <div class="flex items-center mb-2 sm:mb-0">
          <div class="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mr-3">
            <i data-feather="calendar" class="w-5 h-5 text-blue-600 dark:text-blue-400"></i>
          </div>
          <div>
            <h3 class="text-lg font-semibold text-slate-900 dark:text-white">Stay Details</h3>
            <p class="text-sm text-gray-600 dark:text-gray-400">Your travel dates and duration</p>
          </div>
        </div>
        <button
          type="button"
          (click)="editStep(1)"
          class="text-sm text-red-500 hover:text-red-600 font-medium flex items-center px-3 py-1 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
          <i data-feather="edit-2" class="w-4 h-4 mr-1"></i>
          Change Dates
        </button>
      </div>

      <!-- Date Timeline -->
      <div class="bg-white dark:bg-slate-800 rounded-lg p-6 border border-gray-100 dark:border-gray-700">
        <div class="flex flex-col sm:flex-row items-center justify-between">
          <!-- Check-in -->
          <div class="text-center mb-4 sm:mb-0">
            <div class="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-3">
              <i data-feather="log-in" class="w-8 h-8 text-green-600 dark:text-green-400"></i>
            </div>
            <p class="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Check-in</p>
            <p class="text-lg font-bold text-slate-900 dark:text-white">
              {{ bookingData.dateRange?.start | date:'MMM dd' }}
            </p>
            <p class="text-sm text-gray-600 dark:text-gray-400">
              {{ bookingData.dateRange?.start | date:'EEEE' }}
            </p>
            <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {{ bookingData.dateRange?.start | date:'yyyy' }}
            </p>
          </div>

          <!-- Duration Arrow -->
          <div class="flex flex-col items-center mx-4 sm:mx-8">
            <div class="hidden sm:block w-24 h-0.5 bg-gradient-to-r from-green-400 to-red-400 mb-2"></div>
            <div class="sm:hidden w-0.5 h-8 bg-gradient-to-b from-green-400 to-red-400 mb-2"></div>
            <div class="bg-blue-100 dark:bg-blue-900/30 px-3 py-1 rounded-full">
              <span class="text-sm font-semibold text-blue-700 dark:text-blue-300">
                {{ nights }} night{{ nights > 1 ? 's' : '' }}
              </span>
            </div>
            <div class="hidden sm:block w-24 h-0.5 bg-gradient-to-r from-green-400 to-red-400 mt-2"></div>
            <div class="sm:hidden w-0.5 h-8 bg-gradient-to-b from-green-400 to-red-400 mt-2"></div>
          </div>

          <!-- Check-out -->
          <div class="text-center mt-4 sm:mt-0">
            <div class="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-3">
              <i data-feather="log-out" class="w-8 h-8 text-red-600 dark:text-red-400"></i>
            </div>
            <p class="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Check-out</p>
            <p class="text-lg font-bold text-slate-900 dark:text-white">
              {{ bookingData.dateRange?.end | date:'MMM dd' }}
            </p>
            <p class="text-sm text-gray-600 dark:text-gray-400">
              {{ bookingData.dateRange?.end | date:'EEEE' }}
            </p>
            <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {{ bookingData.dateRange?.end | date:'yyyy' }}
            </p>
          </div>
        </div>

        <!-- Additional Info -->
        <div class="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
          <div class="grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
            <div>
              <p class="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Check-in Time</p>
              <p class="text-sm font-medium text-slate-900 dark:text-white">3:00 PM</p>
            </div>
            <div>
              <p class="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Check-out Time</p>
              <p class="text-sm font-medium text-slate-900 dark:text-white">11:00 AM</p>
            </div>
            <div>
              <p class="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Total Days</p>
              <p class="text-sm font-medium text-slate-900 dark:text-white">{{ nights + 1 }}</p>
            </div>
            <div>
              <p class="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Season</p>
              <p class="text-sm font-medium text-slate-900 dark:text-white">Standard</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Accommodation Card -->
    <div *ngIf="bookingData.selectedRooms && bookingData.selectedRooms.length > 0"
         class="border border-gray-200 dark:border-gray-700 rounded-lg p-4 sm:p-6 bg-gradient-to-r from-green-50 to-white dark:from-green-900/10 dark:to-slate-900">
      <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <div class="flex items-center mb-2 sm:mb-0">
          <div class="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mr-3">
            <i data-feather="home" class="w-5 h-5 text-green-600 dark:text-green-400"></i>
          </div>
          <div>
            <h3 class="text-lg font-semibold text-slate-900 dark:text-white">Your Accommodation</h3>
            <p class="text-sm text-gray-600 dark:text-gray-400">Selected rooms and amenities</p>
          </div>
        </div>
        <button
          type="button"
          (click)="editStep(2)"
          class="text-sm text-red-500 hover:text-red-600 font-medium flex items-center px-3 py-1 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
          <i data-feather="edit-2" class="w-4 h-4 mr-1"></i>
          Change Rooms
        </button>
      </div>

      <div class="space-y-6">
        <div *ngFor="let room of bookingData.selectedRooms; let i = index"
             class="bg-white dark:bg-slate-800 rounded-lg border border-gray-100 dark:border-gray-700 overflow-hidden">

          <!-- Room Header -->
          <div class="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-slate-700 dark:to-slate-800 px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <div class="flex items-center mb-2 sm:mb-0">
                <div class="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">
                  {{ i + 1 }}
                </div>
                <div>
                  <h4 class="text-lg font-semibold text-slate-900 dark:text-white">{{ room.roomName }}</h4>
                  <p class="text-sm text-gray-600 dark:text-gray-400">{{ room.roomType }}</p>
                </div>
              </div>
              <div class="text-right">
                <div class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                  {{ room.quantity }} Room{{ room.quantity > 1 ? 's' : '' }}
                </div>
              </div>
            </div>
          </div>

          <!-- Room Details -->
          <div class="p-6">
            <!-- Features Grid -->
            <div *ngIf="room.features && room.features.length > 0" class="mb-6">
              <h5 class="text-sm font-medium text-gray-900 dark:text-white mb-3 flex items-center">
                <i data-feather="check-circle" class="w-4 h-4 mr-2 text-green-600"></i>
                Included Amenities
              </h5>
              <div class="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <div *ngFor="let feature of room.features"
                     class="flex items-center p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                  <i data-feather="check" class="w-4 h-4 text-green-600 dark:text-green-400 mr-2 flex-shrink-0"></i>
                  <span class="text-sm text-green-800 dark:text-green-200">{{ feature }}</span>
                </div>
              </div>
            </div>

            <!-- Pricing Breakdown -->
            <div class="bg-gray-50 dark:bg-slate-700 rounded-lg p-4">
              <h5 class="text-sm font-medium text-gray-900 dark:text-white mb-3 flex items-center">
                <i data-feather="calculator" class="w-4 h-4 mr-2 text-blue-600"></i>
                Pricing Details
              </h5>
              <div class="space-y-2">
                <div class="flex justify-between text-sm">
                  <span class="text-gray-600 dark:text-gray-400">Base Rate (per night)</span>
                  <span class="font-medium text-slate-900 dark:text-white">${{ room.pricePerNight }}</span>
                </div>
                <div class="flex justify-between text-sm">
                  <span class="text-gray-600 dark:text-gray-400">Number of Rooms</span>
                  <span class="font-medium text-slate-900 dark:text-white">{{ room.quantity }}</span>
                </div>
                <div class="flex justify-between text-sm">
                  <span class="text-gray-600 dark:text-gray-400">Number of Nights</span>
                  <span class="font-medium text-slate-900 dark:text-white">{{ nights }}</span>
                </div>
                <div class="border-t border-gray-200 dark:border-gray-600 pt-2 mt-2">
                  <div class="flex justify-between">
                    <span class="text-sm font-medium text-gray-900 dark:text-white">Room Total</span>
                    <span class="text-lg font-bold text-green-600 dark:text-green-400">${{ getRoomTotal(room) }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Room Policies -->
            <div class="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <h5 class="text-sm font-medium text-blue-900 dark:text-blue-200 mb-2 flex items-center">
                <i data-feather="info" class="w-4 h-4 mr-2"></i>
                Room Policies
              </h5>
              <ul class="text-sm text-blue-800 dark:text-blue-300 space-y-1">
                <li>• Maximum occupancy: {{ room.maxOccupancy }} guests</li>
                <li>• Non-smoking room</li>
                <li>• Free cancellation until 24 hours before check-in</li>
                <li>• Late check-out available (subject to availability)</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Additional Services Card -->
    <div *ngIf="selectedServicesCount > 0"
         class="border border-gray-200 dark:border-gray-700 rounded-lg p-4 sm:p-6">
      <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4">
        <h3 class="text-lg font-semibold text-slate-900 dark:text-white mb-2 sm:mb-0">Additional Services</h3>
        <button
          type="button"
          (click)="editStep(3)"
          class="text-sm text-red-500 hover:text-red-600 font-medium flex items-center">
          <i data-feather="edit-2" class="w-4 h-4 mr-1"></i>
          Edit
        </button>
      </div>

      <div class="space-y-3">
        <div *ngFor="let service of selectedServices"
             class="flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 bg-gray-50 dark:bg-slate-800 rounded-lg">
          <div class="mb-2 sm:mb-0">
            <h4 class="font-medium text-slate-900 dark:text-white">{{ service.name }}</h4>
            <p class="text-sm text-gray-600 dark:text-gray-400">{{ service.description }}</p>
          </div>
          <div class="text-right">
            <p class="text-sm text-gray-600 dark:text-gray-400">
              {{ service.quantity || 1 }} × ${{ service.price }}/night
            </p>
            <p class="font-semibold text-slate-900 dark:text-white">
              ${{ getServiceTotal(service) }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Price Summary Card -->
    <div class="border border-gray-200 dark:border-gray-700 rounded-lg p-4 sm:p-6 bg-gray-50 dark:bg-slate-800">
      <h3 class="text-lg font-semibold text-slate-900 dark:text-white mb-4">Price Summary</h3>

      <div class="space-y-3">
        <div class="flex justify-between text-sm">
          <span class="text-gray-600 dark:text-gray-400">Accommodation ({{ nights }} nights)</span>
          <span class="text-slate-900 dark:text-white font-medium">
            ${{ getAccommodationTotal() | number:'1.2-2' }}
          </span>
        </div>

        <div *ngIf="selectedServicesCount > 0" class="flex justify-between text-sm">
          <span class="text-gray-600 dark:text-gray-400">Additional Services</span>
          <span class="text-slate-900 dark:text-white font-medium">
            ${{ getServicesTotal() | number:'1.2-2' }}
          </span>
        </div>

        <div class="flex justify-between text-sm">
          <span class="text-gray-600 dark:text-gray-400">Subtotal</span>
          <span class="text-slate-900 dark:text-white font-medium">${{ (bookingData.subtotal || 0) | number:'1.2-2' }}</span>
        </div>

        <div class="flex justify-between text-sm">
          <span class="text-gray-600 dark:text-gray-400">Taxes & Fees</span>
          <span class="text-slate-900 dark:text-white font-medium">${{ (bookingData.taxes || 0) | number:'1.2-2' }}</span>
        </div>

        <div class="border-t border-gray-200 dark:border-gray-700 pt-3">
          <div class="flex justify-between text-lg font-bold">
            <span class="text-slate-900 dark:text-white">Total</span>
            <span class="text-red-500">${{ (bookingData.total || 0) | number:'1.2-2' }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Action Buttons -->
  <div class="flex flex-col sm:flex-row gap-3 sm:justify-between pt-6 mt-6 border-t border-gray-200 dark:border-gray-700">
    <button
      type="button"
      (click)="onPrevious()"
      class="w-full sm:w-auto py-3 px-6 inline-block tracking-wide align-middle duration-500 text-base text-center bg-gray-500 hover:bg-gray-600 text-white rounded-md cursor-pointer order-2 sm:order-1">
      <i data-feather="arrow-left" class="w-4 h-4 mr-2 inline"></i>
      Previous
    </button>

    <button
      type="button"
      (click)="onNext()"
      class="w-full sm:w-auto py-3 px-6 inline-block tracking-wide align-middle duration-500 text-base text-center bg-red-500 hover:bg-red-600 text-white rounded-md cursor-pointer order-1 sm:order-2">
      Proceed to Payment
      <i data-feather="arrow-right" class="w-4 h-4 ml-2 inline"></i>
    </button>
  </div>
</div>
